type GroupableField = {
    dataProperty?: string;
    columnName?: string;
};

const TRAILING_DESCRIPTOR_TOKENS = new Set([
    'name',
    'phone',
    'relationship',
    'email',
    'address',
    'number',
    'method',
    'type',
]);

const DESCRIPTOR_PRIORITY: Record<string, number> = {
    name: 0,
    phone: 1,
    email: 2,
    address: 3,
    number: 4,
    method: 5,
    type: 6,
    relationship: 7,
};

const tokenize = (value: string): string[] => {
    return value
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
        .replace(/([0-9])([a-zA-Z])/g, '$1 $2')
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter(Boolean);
};

const buildGroupKey = (field: GroupableField, index: number): string => {
    const source = field.dataProperty || field.columnName || '';
    const tokens = tokenize(source);

    if (!tokens.length) {
        return `field-${index}`;
    }

    const baseTokens = [...tokens];
    while (
        baseTokens.length > 1 &&
        TRAILING_DESCRIPTOR_TOKENS.has(baseTokens[baseTokens.length - 1])
    ) {
        baseTokens.pop();
    }

    return baseTokens.join(' ');
};

export const groupFieldsByNearestName = <T extends GroupableField>(
    fields: T[],
): T[] => {
    if (!Array.isArray(fields) || !fields.length) {
        return [];
    }

    const grouped = new Map<string, { firstIndex: number; items: T[] }>();

    fields.forEach((field, index) => {
        const key = buildGroupKey(field, index);
        const existingGroup = grouped.get(key);

        if (existingGroup) {
            existingGroup.items.push(field);
            return;
        }

        grouped.set(key, {
            firstIndex: index,
            items: [field],
        });
    });

    const getDescriptorPriority = (field: T): number => {
        const source = field.dataProperty || field.columnName || '';
        const tokens = tokenize(source);
        const lastToken = tokens[tokens.length - 1] ?? '';
        return DESCRIPTOR_PRIORITY[lastToken] ?? 99;
    };

    const extractNumericSuffix = (key: string): { base: string; num: number } => {
        const match = /^(.*?)\s*(\d+)$/.exec(key);
        if (match) {
            return { base: match[1].trim(), num: parseInt(match[2], 10) };
        }
        return { base: key, num: -1 };
    };

    const baseAnchorIndex = new Map<string, number>();
    for (const [key, group] of grouped.entries()) {
        const { base } = extractNumericSuffix(key);
        const current = baseAnchorIndex.get(base);
        if (current === undefined || group.firstIndex < current) {
            baseAnchorIndex.set(base, group.firstIndex);
        }
    }

    return [...grouped.entries()]
        .sort(([keyA, groupA], [keyB, groupB]) => {
            const a = extractNumericSuffix(keyA);
            const b = extractNumericSuffix(keyB);
            const aAnchor = baseAnchorIndex.get(a.base) ?? groupA.firstIndex;
            const bAnchor = baseAnchorIndex.get(b.base) ?? groupB.firstIndex;
            if (aAnchor !== bAnchor) {
                return aAnchor - bAnchor;
            }
            return a.num - b.num;
        })
        .flatMap(([, group]) =>
            group.items.slice().sort(
                (a, b) => getDescriptorPriority(a) - getDescriptorPriority(b),
            ),
        );
};